/**
 * When the `sign_up` conversion fires (design 2026-09-22 §5.2, M7).
 *
 * The account is created on `/sign-up`, an analytics-excluded page where no
 * push is delivered, and Better Auth answers that form with success even for an
 * existing email, so creation itself is not a usable signal. The event fires on
 * the first tracked page instead: for an account at most
 * SIGN_UP_CONVERSION_WINDOW_MS old (email signups, method `email`), or one that
 * arrives with `?signed-up=1` (social and magic-link signups). Once per browser
 * per user, through the marker below.
 */
export const SIGN_UP_SENT_STORAGE_KEY = 'IsActiveSessionSignedUp'
export const SIGN_UP_CONVERSION_WINDOW_MS = 7 * 24 * 60 * 60 * 1000

export type SignUpTrigger = 'query' | 'recent_account'

export function signUpTrigger(input: {
  userId: string | null | undefined
  createdAt: string | null | undefined
  signedUpQuery: boolean
  alreadySentFor: string | null
  nowMs: number
}): SignUpTrigger | null {
  if (!input.userId) return null
  if (input.alreadySentFor === input.userId) return null
  if (input.signedUpQuery) return 'query'
  if (!input.createdAt) return null
  const createdMs = Date.parse(input.createdAt)
  if (Number.isNaN(createdMs)) return null
  return input.nowMs - createdMs <= SIGN_UP_CONVERSION_WINDOW_MS ? 'recent_account' : null
}

let sentInThisDocument: string | null = null

export function readSignUpSentFor(storage: Pick<Storage, 'getItem'> | null): string | null {
  try {
    return storage?.getItem(SIGN_UP_SENT_STORAGE_KEY) ?? sentInThisDocument
  } catch {
    return sentInThisDocument
  }
}

export function recordSignUpSent(userId: string, storage: Pick<Storage, 'setItem'> | null): void {
  sentInThisDocument = userId
  try {
    storage?.setItem(SIGN_UP_SENT_STORAGE_KEY, userId)
  } catch {
    // Safari Private / quota exceeded: the in-document marker still holds.
  }
}

/** Test seam: forget the in-document marker. */
export function _resetSignUpSentForTests(): void {
  sentInThisDocument = null
}
